import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";

type MilestoneType = "6_month" | "12_month" | "24_month" | "annual";

interface Milestone {
  id: string;
  policy_id: string;
  milestone_type: MilestoneType;
  milestone_date: string;
  task_id: string | null;
  email_sent_at: string | null;
  email_log_id: string | null;
  client_responded_at: string | null;
  appointment_id: string | null;
  completed_at: string | null;
  skipped_at: string | null;
  skip_reason: string | null;
  created_at: string;
}

interface PolicyLite {
  id: string;
  client_id: string;
  agent_id: string | null;
  product_type: string | null;
  effective_date: string | null;
}

const MILESTONE_LABEL: Record<MilestoneType, string> = {
  "6_month": "6-month",
  "12_month": "12-month",
  "24_month": "24-month",
  annual: "Annual",
};

type Tab = "upcoming" | "in_progress" | "completed" | "skipped";

function daysBetween(a: string, b: string) {
  const ms = new Date(a + "T00:00:00Z").getTime() - new Date(b + "T00:00:00Z").getTime();
  return Math.round(ms / 86400000);
}

export const Route = createFileRoute("/admin/renewals")({
  head: () => ({
    meta: [
      { title: "Renewals — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Command Center" nav={ADMIN_NAV} tourKey="admin_tour">
        <AdminRenewals />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AdminRenewals() {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [policies, setPolicies] = useState<Map<string, PolicyLite>>(new Map());
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [agentNames, setAgentNames] = useState<Map<string, string>>(new Map());
  const [tab, setTab] = useState<Tab>("upcoming");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [agentFilter, setAgentFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    const ms = await supabase
      .from("policy_review_milestones")
      .select("*")
      .order("milestone_date", { ascending: true });
    const rows = (ms.data ?? []) as Milestone[];
    setMilestones(rows);

    const policyIds = Array.from(new Set(rows.map((r) => r.policy_id)));
    if (policyIds.length > 0) {
      const { data: pols } = await supabase
        .from("policies")
        .select("id, client_id, agent_id, product_type, effective_date")
        .in("id", policyIds);
      const pm = new Map<string, PolicyLite>();
      const clientIds = new Set<string>();
      const agentIds = new Set<string>();
      for (const p of (pols ?? []) as PolicyLite[]) {
        pm.set(p.id, p);
        clientIds.add(p.client_id);
        if (p.agent_id) agentIds.add(p.agent_id);
      }
      setPolicies(pm);

      if (clientIds.size > 0) {
        const { data: cli } = await supabase
          .from("clients")
          .select("id, user_id")
          .in("id", Array.from(clientIds));
        const userIds = (cli ?? []).map((c: any) => c.user_id).filter(Boolean);
        const allProfileIds = Array.from(new Set([...userIds, ...Array.from(agentIds)]));
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", allProfileIds);
        const pn = new Map<string, string>();
        for (const p of (profs ?? []) as Array<{ id: string; full_name: string | null }>) {
          pn.set(p.id, p.full_name || "Unnamed");
        }
        const cn = new Map<string, string>();
        for (const c of (cli ?? []) as Array<{ id: string; user_id: string }>) {
          cn.set(c.id, pn.get(c.user_id) || "Client");
        }
        setClientNames(cn);
        const an = new Map<string, string>();
        for (const id of agentIds) an.set(id, pn.get(id) || "Strategist");
        setAgentNames(an);
      }
    } else {
      setPolicies(new Map());
      setClientNames(new Map());
      setAgentNames(new Map());
    }
    setLoading(false);
  }

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    return milestones.filter((m) => {
      // Tab filtering
      if (tab === "upcoming") {
        if (m.completed_at || m.skipped_at) return false;
        if (m.milestone_date < today) return false;
      } else if (tab === "in_progress") {
        if (!m.email_sent_at || m.completed_at || m.skipped_at) return false;
      } else if (tab === "completed") {
        if (!m.completed_at) return false;
      } else if (tab === "skipped") {
        if (!m.skipped_at) return false;
      }
      if (typeFilter !== "all" && m.milestone_type !== typeFilter) return false;
      const policy = policies.get(m.policy_id);
      if (agentFilter !== "all" && policy?.agent_id !== agentFilter) return false;
      if (q) {
        const clientName = (clientNames.get(policy?.client_id || "") || "").toLowerCase();
        if (!clientName.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [milestones, tab, typeFilter, agentFilter, q, policies, clientNames, today]);

  // KPIs for this month
  const monthKey = today.slice(0, 7);
  const kpis = useMemo(() => {
    const thisMonth = milestones.filter((m) => m.milestone_date.startsWith(monthKey));
    return {
      total: thisMonth.length,
      completed: thisMonth.filter((m) => m.completed_at).length,
      pending: thisMonth.filter((m) => !m.completed_at && !m.skipped_at).length,
      overdue: milestones.filter(
        (m) => !m.completed_at && !m.skipped_at && m.milestone_date < today,
      ).length,
    };
  }, [milestones, monthKey, today]);

  const agentOptions = useMemo(() => {
    return Array.from(agentNames.entries()).map(([id, name]) => ({ id, name }));
  }, [agentNames]);

  const openMilestone = openId ? milestones.find((m) => m.id === openId) || null : null;

  return (
    <PortalPage
      title="Policy Reviews"
      eyebrow="Renewals"
      intro="Track every 6, 12, 24-month and annual review across the agency."
    >
      {/* KPI row */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="This month" value={kpis.total} />
        <KpiCard label="Completed" value={kpis.completed} accent="success" />
        <KpiCard label="Pending" value={kpis.pending} />
        <KpiCard label="Overdue" value={kpis.overdue} accent="danger" />
      </div>

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-2 border-b border-bronze-600/30">
        {(["upcoming", "in_progress", "completed", "skipped"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] ${
              tab === t
                ? "border-b-2 border-bronze-600 text-navy-900"
                : "text-charcoal-500 hover:text-navy-800"
            }`}
          >
            {t.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search by client name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="6_month">6-month</SelectItem>
            <SelectItem value="12_month">12-month</SelectItem>
            <SelectItem value="24_month">24-month</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All strategists" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All strategists</SelectItem>
            {agentOptions.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-bronze-600/20 bg-white shadow-soft">
        <table className="w-full font-sans text-[13px]">
          <thead className="bg-cream-50 text-left text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
            <tr>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Strategist</th>
              <th className="px-3 py-2">Policy</th>
              <th className="px-3 py-2">Milestone</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-charcoal-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-charcoal-500">
                  No milestones match this view.
                </td>
              </tr>
            ) : (
              filtered.map((m) => {
                const policy = policies.get(m.policy_id);
                const clientName = clientNames.get(policy?.client_id || "") || "—";
                const agentName = policy?.agent_id ? agentNames.get(policy.agent_id) : "Unassigned";
                const dr = daysBetween(m.milestone_date, today);
                const drLabel =
                  dr === 0 ? "today" : dr > 0 ? `in ${dr}d` : `${Math.abs(dr)}d ago`;
                return (
                  <tr
                    key={m.id}
                    onClick={() => setOpenId(m.id)}
                    className="cursor-pointer border-t border-bronze-600/15 hover:bg-cream-50/60"
                  >
                    <td className="px-3 py-2 text-navy-900">{clientName}</td>
                    <td className="px-3 py-2 text-charcoal-900">{agentName}</td>
                    <td className="px-3 py-2 text-charcoal-900">{policy?.product_type || "—"}</td>
                    <td className="px-3 py-2 text-charcoal-900">
                      {MILESTONE_LABEL[m.milestone_type]}
                    </td>
                    <td className="px-3 py-2 text-charcoal-900">
                      {m.milestone_date} <span className="text-charcoal-500">· {drLabel}</span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill m={m} today={today} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {openMilestone && (
        <MilestoneDrawer
          milestone={openMilestone}
          policy={policies.get(openMilestone.policy_id) || null}
          clientName={clientNames.get(policies.get(openMilestone.policy_id)?.client_id || "") || "—"}
          agentName={
            policies.get(openMilestone.policy_id)?.agent_id
              ? agentNames.get(policies.get(openMilestone.policy_id)!.agent_id!) || "—"
              : "Unassigned"
          }
          agentOptions={agentOptions}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            setOpenId(null);
            void load();
          }}
        />
      )}
    </PortalPage>
  );
}

function KpiCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "success" | "danger";
}) {
  const color =
    accent === "success"
      ? "text-[#0E7C5A]"
      : accent === "danger"
        ? "text-[#B43A3A]"
        : "text-navy-900";
  return (
    <div className="border border-bronze-600/30 bg-white p-4 shadow-soft">
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        {label}
      </p>
      <p className={`mt-1 font-display text-[32px] ${color}`}>{value}</p>
    </div>
  );
}

function StatusPill({ m, today }: { m: Milestone; today: string }) {
  if (m.completed_at) {
    return (
      <span className="inline-flex bg-[#0E7C5A]/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0E7C5A]">
        Completed
      </span>
    );
  }
  if (m.skipped_at) {
    return (
      <span className="inline-flex bg-charcoal-500/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-charcoal-500">
        Skipped
      </span>
    );
  }
  if (m.appointment_id) {
    return (
      <span className="inline-flex bg-bronze-400/30 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-900">
        Booked
      </span>
    );
  }
  if (m.email_sent_at) {
    return (
      <span className="inline-flex bg-navy-900/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-900">
        Email sent
      </span>
    );
  }
  if (m.milestone_date < today) {
    return (
      <span className="inline-flex bg-[#B43A3A]/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#B43A3A]">
        Overdue
      </span>
    );
  }
  return (
    <span className="inline-flex bg-cream-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-charcoal-500">
      Pending
    </span>
  );
}

function MilestoneDrawer({
  milestone,
  policy,
  clientName,
  agentName,
  agentOptions,
  onClose,
  onChanged,
}: {
  milestone: Milestone;
  policy: PolicyLite | null;
  clientName: string;
  agentName: string;
  agentOptions: Array<{ id: string; name: string }>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [reassignTo, setReassignTo] = useState<string>("");
  const [busy, setBusy] = useState(false);

  async function markCompleted() {
    setBusy(true);
    const { error } = await supabase
      .from("policy_review_milestones")
      .update({ completed_at: new Date().toISOString() })
      .eq("id", milestone.id);
    if (!error) {
      await supabase.from("activity_logs").insert({
        action: "policy.review_completed",
        target_type: "policy_review_milestone",
        target_id: milestone.id,
        metadata: { policy_id: milestone.policy_id },
      });
      toast.success("Milestone marked completed");
      onChanged();
    } else {
      toast.error(error.message);
    }
    setBusy(false);
  }

  async function skipMilestone() {
    if (!skipReason.trim()) {
      toast.error("Skip reason required");
      return;
    }
    setBusy(true);
    const { error } = await supabase
      .from("policy_review_milestones")
      .update({ skipped_at: new Date().toISOString(), skip_reason: skipReason.trim() })
      .eq("id", milestone.id);
    if (!error) {
      await supabase.from("activity_logs").insert({
        action: "policy.review_skipped",
        target_type: "policy_review_milestone",
        target_id: milestone.id,
        metadata: { policy_id: milestone.policy_id, reason: skipReason.trim() },
      });
      toast.success("Milestone skipped");
      onChanged();
    } else {
      toast.error(error.message);
    }
    setBusy(false);
  }

  async function reassign() {
    if (!reassignTo || !milestone.task_id) return;
    setBusy(true);
    const { error } = await supabase
      .from("tasks")
      .update({ assignee_id: reassignTo })
      .eq("id", milestone.task_id);
    if (!error) {
      toast.success("Task reassigned");
      onChanged();
    } else {
      toast.error(error.message);
    }
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-charcoal-900/40" />
      <div
        className="w-full max-w-md overflow-y-auto bg-cream-50 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          Policy Review
        </p>
        <h2 className="mt-1 font-display text-[24px] text-navy-900">
          {clientName} · {MILESTONE_LABEL[milestone.milestone_type]}
        </h2>
        <p className="mt-1 font-sans text-[13px] text-charcoal-500">
          {policy?.product_type || "Policy"} · strategist: {agentName}
        </p>

        <div className="mt-5 space-y-2 border-l-2 border-bronze-600/40 pl-4 font-sans text-[13px]">
          <TimelineRow label="Milestone created" at={milestone.created_at} />
          <TimelineRow label="Email sent" at={milestone.email_sent_at} />
          <TimelineRow label="Appointment booked" at={milestone.appointment_id ? "—" : null} />
          <TimelineRow label="Completed" at={milestone.completed_at} />
          {milestone.skipped_at && (
            <TimelineRow
              label={`Skipped — ${milestone.skip_reason || ""}`}
              at={milestone.skipped_at}
            />
          )}
        </div>

        {!milestone.completed_at && !milestone.skipped_at && (
          <div className="mt-6 space-y-3">
            <PrimaryButton onClick={() => void markCompleted()} disabled={busy}>
              Mark Completed
            </PrimaryButton>
            {!skipOpen ? (
              <button
                onClick={() => setSkipOpen(true)}
                className="block w-full border border-bronze-600 px-4 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-navy-800"
              >
                Skip Milestone
              </button>
            ) : (
              <div className="space-y-2 border border-bronze-600/40 bg-white p-3">
                <Input
                  placeholder="Reason for skipping"
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => void skipMilestone()}
                    disabled={busy}
                    className="flex-1 bg-navy-800 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-cream-50"
                  >
                    Confirm Skip
                  </button>
                  <button
                    onClick={() => setSkipOpen(false)}
                    className="px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {milestone.task_id && agentOptions.length > 0 && (
              <div className="border border-bronze-600/40 bg-white p-3">
                <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500">
                  Reassign task
                </p>
                <div className="mt-2 flex gap-2">
                  <Select value={reassignTo} onValueChange={setReassignTo}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select strategist" />
                    </SelectTrigger>
                    <SelectContent>
                      {agentOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => void reassign()}
                    disabled={!reassignTo || busy}
                    className="bg-bronze-600 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy-900 disabled:opacity-50"
                  >
                    Reassign
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-6 w-full border border-charcoal-500/40 px-4 py-2 font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function TimelineRow({ label, at }: { label: string; at: string | null }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={at ? "text-navy-900" : "text-charcoal-500"}>{label}</span>
      <span className="text-[12px] text-charcoal-500">
        {at ? new Date(at).toLocaleString() : "—"}
      </span>
    </div>
  );
}